import type { PlaybookStep } from '@chatsdet/types';
import type { RunCodeToolCall } from './step-linker.js';

export interface TimelineCacheCodeEvidence {
  action: string;
  code: string;
  toolName?: RunCodeToolCall['toolName'];
}

export type RunCodeSource = 'playbookStep' | 'executedPlaybook' | 'thoughts' | 'timeline-actions';

export interface RunCodeEnrichmentInput {
  playbookSteps: readonly PlaybookStep[];
  runStep?: { metadataJson?: unknown };
  runStepMetadata?: unknown;
  thoughtCacheCode?: string;
  timelineCacheCode?: readonly TimelineCacheCodeEvidence[];
}

export interface RunCodeEnrichmentResult {
  playbookSteps: PlaybookStep[];
  runCodeSourcesByStepOrder: Record<number, RunCodeSource>;
  runCodeToolCalls: RunCodeToolCall[];
}

export function enrichPlaybookStepsWithRunCode(
  args: RunCodeEnrichmentInput
): RunCodeEnrichmentResult {
  const sources: Record<number, RunCodeSource> = {};
  const runCodeToolCalls: RunCodeToolCall[] = [];
  const usedTimelineIndices = new Set<number>();
  const timelineCacheCode = args.timelineCacheCode ?? [];
  const runStepMetadata =
    'runStepMetadata' in args ? args.runStepMetadata : args.runStep?.metadataJson;

  const playbookSteps = args.playbookSteps.map((step) => {
    if (typeof step.cacheCode === 'string' && step.cacheCode.trim()) {
      sources[step.order] = 'playbookStep';
      return step;
    }

    const executedCode = cacheCodeFromExecutedPlaybook(runStepMetadata, step.order);
    if (executedCode) {
      sources[step.order] = 'executedPlaybook';
      runCodeToolCalls.push({ stepOrder: step.order, toolName: 'browser_run_code' });
      return { ...step, cacheCode: executedCode };
    }

    if (args.thoughtCacheCode) {
      sources[step.order] = 'thoughts';
      runCodeToolCalls.push({ stepOrder: step.order, toolName: 'browser_run_code' });
      return { ...step, cacheCode: args.thoughtCacheCode };
    }

    const timelineEntryIndex = timelineCacheCode.findIndex((entry, index) => {
      if (usedTimelineIndices.has(index)) return false;
      return timelineEvidenceMatchesStep(entry, step);
    });
    if (timelineEntryIndex >= 0) {
      const timelineEntry = timelineCacheCode[timelineEntryIndex];
      usedTimelineIndices.add(timelineEntryIndex);
      sources[step.order] = 'timeline-actions';
      runCodeToolCalls.push({
        stepOrder: step.order,
        toolName: timelineEntry.toolName ?? 'browser_run_code',
      });
      return { ...step, cacheCode: timelineEntry.code };
    }

    return step;
  });

  return { playbookSteps, runCodeSourcesByStepOrder: sources, runCodeToolCalls };
}

export function timelineEvidenceMatchesStep(
  evidence: TimelineCacheCodeEvidence,
  step: PlaybookStep
): boolean {
  switch (step.action) {
    case 'fill':
    case 'fill_form':
      return evidence.action === 'type' || evidence.action === 'fill';
    case 'click':
    case 'toggle':
      return ['click', 'check', 'uncheck'].includes(evidence.action);
    case 'navigate':
      return evidence.action === 'navigate';
    case 'select':
      return evidence.action === 'select';
    case 'press_key':
      return (
        evidence.action === 'press' ||
        evidence.action === 'press_key' ||
        evidence.action === 'press-key'
      );
    case 'hover':
      return evidence.action === 'hover';
    case 'upload':
      return evidence.action === 'upload' || evidence.action === 'file-upload';
    case 'drag':
      return evidence.action === 'drag';
    default:
      return false;
  }
}

export function timelineActionCode(raw: Record<string, unknown>, action: string): string | null {
  const target = stringField(raw, 'target');
  if (action === 'navigate') {
    const url = stringField(raw, 'url') ?? target;
    return url ? `await page.goto(${jsArg(url)});` : null;
  }

  if (!target) return null;
  const locator = pageLocatorExpression(target);
  if (!locator) return null;

  switch (action) {
    case 'type':
    case 'fill': {
      const value = stringField(raw, 'value');
      return value !== null ? `await ${locator}.fill(${jsArg(value)});` : null;
    }
    case 'click':
      return `await ${locator}.click();`;
    case 'check':
      return `await ${locator}.check();`;
    case 'uncheck':
      return `await ${locator}.uncheck();`;
    case 'hover':
      return `await ${locator}.hover();`;
    case 'press':
    case 'press_key':
    case 'press-key': {
      const value = stringField(raw, 'value') ?? stringField(raw, 'key');
      return value ? `await ${locator}.press(${jsArg(value)});` : null;
    }
    case 'select': {
      const value = stringField(raw, 'value');
      return value ? `await ${locator}.selectOption(${jsArg(value)});` : null;
    }
    default:
      return null;
  }
}

function cacheCodeFromExecutedPlaybook(metadata: unknown, stepOrder: number): string | null {
  const meta = isObject(metadata) ? metadata : {};
  const executed = Array.isArray(meta.executedPlaybook) ? meta.executedPlaybook : [];
  for (const raw of executed) {
    if (!isObject(raw)) continue;
    if (raw.order !== stepOrder) continue;
    const cacheCode = stringField(raw, 'cacheCode') ?? stringField(raw, 'code');
    if (cacheCode?.trim()) return cacheCode;
  }
  return null;
}

function pageLocatorExpression(target: string): string | null {
  const trimmed = target.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('page.')) return trimmed;
  if (/^(getBy|locator\(|frameLocator\()/.test(trimmed)) return `page.${trimmed}`;
  return null;
}

function jsArg(value: string): string {
  return /^vars\.[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/.test(value)
    ? value
    : JSON.stringify(value);
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
